/**
 * Campaign-level Tool (SMSWiser) stats. View a single drop's native performance or
 * aggregate the group ("All drops"). The drop list comes from one of two sources:
 * "With clicks" (drops in the Binom click report for the date) or "All sent" (every
 * drop the app sent, click-independent) — so a 0-click drop is still reachable.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoadingSpinner, SearchableSelect } from '@/components/ui';
import {
  DateRangePresetDropdown,
  toRangeISO,
  useStatsTabState,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

import { InfoHint } from './shared/InfoHint';
import { useToolDropList, type DropSource } from './shared/useToolDropList';
import { StatsSourceToggle } from './StatsSourceToggle';
import { ToolStatsView } from './ToolStatsView';
import { ToolStatsMultiView } from './ToolStatsMultiView';

interface ToolStatsTabProps {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
}

// Each shown drop fires its own native-stats request, so let the user pick how
// many to load at once (default 50) instead of a hard cap. 0 = All (slower).
const DROP_LIMIT_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_DROP_LIMIT = 50;

export const ToolStatsTab: FC<ToolStatsTabProps> = ({ countryKey, wiserIds, workDate }) => {
  const state = useStatsTabState(workDate, 'drops');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  // Deep-link from the drops popup: /smswiser/<country>?src=tool&drop=<uniqueId>.
  // Default to "All sent" so the drop is in the list (it may have no clicks yet).
  const [searchParams] = useSearchParams();
  const dropParam = (searchParams.get('drop') ?? '').trim();

  const [source, setSource] = useState<DropSource>(dropParam ? 'sent' : 'clicks');
  const { drops, isLoading } = useToolDropList({
    countryKey,
    wiserIds,
    source,
    rangeFrom: range.from,
    rangeTo: range.to,
    currency: state.currency,
  });

  const [mode, setMode] = useState<'single' | 'all'>('single');
  const [dropLimit, setDropLimit] = useState<number>(DEFAULT_DROP_LIMIT);
  const [selected, setSelected] = useState(dropParam);
  useEffect(() => {
    if (drops.length === 0) {
      // Preserve a deep-linked selection through the initial loading phase
      // (drops is briefly empty); only drop a stale user pick once the list is
      // truly empty.
      if (selected && selected !== dropParam) setSelected('');
      return;
    }
    // Keep a deep-linked drop selected even if it's missing from the current
    // source list — ToolStatsView fetches native stats by uniqueId regardless.
    if (selected && selected === dropParam) return;
    if (!drops.some((d) => d.uniqueId === selected)) setSelected(drops[0]!.uniqueId);
  }, [drops, selected, dropParam]);

  const cappedDrops = dropLimit === 0 ? drops : drops.slice(0, dropLimit);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">View</span>
          <StatsSourceToggle
            value={mode}
            onChange={(v) => setMode(v as 'single' | 'all')}
            options={[
              { value: 'single', label: 'Single drop' },
              { value: 'all', label: 'All drops' },
            ]}
          />
        </div>
        {mode === 'all' && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Show</span>
            <select
              value={dropLimit}
              onChange={(e) => setDropLimit(Number(e.target.value))}
              title="How many drops to load native stats for at once"
              className="h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground"
            >
              {DROP_LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} drops</option>
              ))}
              <option value={0}>All ({drops.length})</option>
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            Drops shown
            <InfoHint>
              <span className="block font-semibold text-foreground">With clicks</span>
              Only drops that registered tracked clicks in Binom for the selected date — that’s why a
              drop you sent can be missing here until it gets a click.
              <span className="mt-1.5 block font-semibold text-foreground">All sent</span>
              Every drop in the SMSWiser account for this campaign &amp; date (click-independent), so
              0-click drops show too. Per-drop stats come straight from SMSWiser either way.
            </InfoHint>
          </span>
          <StatsSourceToggle
            value={source}
            onChange={(v) => setSource(v as DropSource)}
            options={[
              { value: 'clicks', label: 'With clicks' },
              { value: 'sent', label: 'All sent' },
            ]}
          />
        </div>
        {mode === 'single' && (
          <div className="min-w-[260px] flex-1">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Drop</label>
            <SearchableSelect
              options={drops.map((d) => ({ value: d.uniqueId, label: d.label }))}
              value={selected}
              onChange={(v) => setSelected(String(v))}
              placeholder={isLoading ? 'Loading drops…' : 'Select a drop'}
              disabled={isLoading || drops.length === 0}
            />
          </div>
        )}
        <DateRangePresetDropdown
          fromDate={state.fromDate}
          toDate={state.toDate}
          onFromChange={state.setFromDate}
          onToChange={state.setToDate}
          onDateRangeChange={state.setDateRange}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      ) : drops.length === 0 ? (
        <p className="px-1 py-8 text-sm text-muted-foreground">
          {source === 'clicks'
            ? 'No drops with tracked clicks in this range. Switch “Drops shown” to “All sent” to see every drop you sent — including 0-click ones — or widen the date range.'
            : 'No sent drops in this range. Try widening the date range.'}
        </p>
      ) : mode === 'all' ? (
        <>
          {dropLimit !== 0 && drops.length > dropLimit && (
            <p className="px-1 text-xs text-amber-600 dark:text-amber-400">
              Showing the first {dropLimit} of {drops.length} drops to keep it fast — raise “Show”
              (slower) or narrow the date range.
            </p>
          )}
          <ToolStatsMultiView drops={cappedDrops} />
        </>
      ) : selected ? (
        <ToolStatsView uniqueId={selected} />
      ) : null}
    </div>
  );
};

export default ToolStatsTab;
